import { useEffect, useRef } from "react";
import { Vibrate, ListChecks, RefreshCw, Paperclip, Smartphone } from "lucide-react";
import Header from "../Header/Header";
import Footer from "../Footer/footer";
import SEOHead from "../SEO/SEOHead";
import { PageHero, SectionHeading, PhoneMock } from "../common/ui";
import "../../css/MainPage.css";
import "../../css/Pages.css";
import "../../css/MobileApp.css";
import googlePlay from "../../../assets/google-play.png";
import appStore from "../../../assets/app-store.png";
import domainScreen from "../../../assets/mobile-app/app-domain.webp";
import loginScreen from "../../../assets/mobile-app/app-login.webp";
import dashboardScreen from "../../../assets/mobile-app/app-dashboard-defects.webp";
import testsDashboardScreen from "../../../assets/mobile-app/app-dashboard-tests.webp";
import requirementsScreen from "../../../assets/mobile-app/app-requirements.webp";
import defectsScreen from "../../../assets/mobile-app/app-defects.webp";

// Sign-in screens from the App Store listing (v1.0.2); the rest show the
// FrothShop Web Store demo project so no customer data appears on the site.
const screens = [domainScreen, loginScreen, dashboardScreen, testsDashboardScreen, requirementsScreen, defectsScreen];

// Decorative micro-visuals under each highlight — pure SVG, no text, one
// per card in the page's blue/violet/pink trio.
const widgetArt = [
  // Shake-to-report: phone with motion arcs.
  <svg viewBox="0 0 220 84" fill="none" aria-hidden="true" key="art-shake">
    <g stroke="rgba(58, 106, 182, 0.45)" strokeWidth="2" strokeLinecap="round">
      <path d="M28 20c-7 6-7 38 0 44" />
      <path d="M15 12c-12 10-12 50 0 60" opacity="0.55" />
      <path d="M192 20c7 6 7 38 0 44" />
      <path d="M205 12c12 10 12 50 0 60" opacity="0.55" />
    </g>
    <rect x="92" y="8" width="36" height="68" rx="9" fill="#fff" stroke="rgba(58, 106, 182, 0.5)" strokeWidth="2" />
    <rect x="101" y="17" width="18" height="3.5" rx="1.75" fill="rgba(58, 106, 182, 0.3)" />
    <rect x="99" y="30" width="22" height="5" rx="2.5" fill="rgba(58, 106, 182, 0.55)" />
    <rect x="99" y="41" width="14" height="5" rx="2.5" fill="rgba(58, 106, 182, 0.3)" />
    <rect x="99" y="52" width="18" height="5" rx="2.5" fill="rgba(58, 106, 182, 0.18)" />
  </svg>,
  // Test listings: rows with ticks, the active row highlighted.
  <svg viewBox="0 0 220 84" fill="none" aria-hidden="true" key="art-list">
    <g>
      <rect x="30" y="8" width="160" height="19" rx="9.5" fill="rgba(108, 79, 246, 0.07)" stroke="rgba(108, 79, 246, 0.28)" />
      <path d="M42 17.5l2.4 2.4 4.2-4.8" stroke="rgba(108, 79, 246, 0.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="58" y="15" width="96" height="5" rx="2.5" fill="rgba(108, 79, 246, 0.32)" />
      <rect x="30" y="32.5" width="160" height="19" rx="9.5" fill="rgba(108, 79, 246, 0.07)" stroke="rgba(108, 79, 246, 0.28)" />
      <path d="M42 42l2.4 2.4 4.2-4.8" stroke="rgba(108, 79, 246, 0.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="58" y="39.5" width="72" height="5" rx="2.5" fill="rgba(108, 79, 246, 0.32)" />
      <rect x="30" y="57" width="160" height="19" rx="9.5" fill="rgba(108, 79, 246, 0.14)" stroke="rgba(108, 79, 246, 0.5)" />
      <circle cx="47" cy="66.5" r="5.5" stroke="rgba(108, 79, 246, 0.7)" strokeWidth="2" />
      <rect x="58" y="64" width="84" height="5" rx="2.5" fill="rgba(108, 79, 246, 0.5)" />
    </g>
  </svg>,
  // Web/mobile sync: phone and monitor trading arrows.
  <svg viewBox="0 0 220 84" fill="none" aria-hidden="true" key="art-sync">
    <rect x="12" y="16" width="28" height="52" rx="8" fill="#fff" stroke="rgba(192, 86, 255, 0.55)" strokeWidth="2" />
    <rect x="19" y="26" width="14" height="4" rx="2" fill="rgba(192, 86, 255, 0.4)" />
    <rect x="19" y="36" width="14" height="4" rx="2" fill="rgba(192, 86, 255, 0.2)" />
    <rect x="152" y="24" width="56" height="38" rx="7" fill="#fff" stroke="rgba(192, 86, 255, 0.55)" strokeWidth="2" />
    <rect x="160" y="33" width="40" height="4" rx="2" fill="rgba(192, 86, 255, 0.4)" />
    <rect x="160" y="43" width="28" height="4" rx="2" fill="rgba(192, 86, 255, 0.2)" />
    <path d="M170 76h20" stroke="rgba(192, 86, 255, 0.55)" strokeWidth="2" strokeLinecap="round" />
    <g stroke="rgba(192, 86, 255, 0.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="4 5">
      <path d="M50 24c30-14 92-14 92 0" />
      <path d="M50 62c30 14 92 14 92 0" />
    </g>
    <path d="M142 20l4 4-6 2" fill="none" stroke="rgba(192, 86, 255, 0.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M78 66l-4-4 6-2" fill="none" stroke="rgba(192, 86, 255, 0.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
];

const highlights = [
  {
    icon: Vibrate,
    title: "Instant bug reporting",
    text: "Shake your phone to capture and report a bug instantly, without leaving what you're doing.",
  },
  {
    icon: ListChecks,
    title: "Comprehensive test listings",
    text: "See test scenarios, test cases and defects in one easy-to-browse list.",
  },
  {
    icon: RefreshCw,
    title: "Seamless web and mobile sync",
    text: "Defects and attachments you add on your phone appear instantly in the web app, and vice versa.",
  },
];

const features = [
  {
    icon: RefreshCw,
    title: "Unified testing across devices",
    text: "Your testing data stays in sync between the mobile and web apps, so everyone works from the same information.",
  },
  {
    icon: Vibrate,
    title: "Shake-to-Report",
    text: "Start a bug report in under a minute, right from the device in your hand.",
  },
  {
    icon: ListChecks,
    title: "All your testing data, organised",
    text: "Test cases, scenarios and defects in a single, streamlined interface.",
  },
  {
    icon: Paperclip,
    title: "Real-time attachments",
    text: "Capture a screenshot, log or file on your phone and attach it straight to a test case or defect.",
  },
];

function StoreBadges() {
  return (
    <div className="mob-badges">
      <a
        href="https://play.google.com/store/apps/details?id=ytlc.frothtestops.com.testops"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img src={googlePlay} alt="Get it on Google Play" />
      </a>
      <a href="https://apps.apple.com/in/app/frothtestops/id6746042634" target="_blank" rel="noopener noreferrer">
        <img src={appStore} alt="Download on the App Store" />
      </a>
    </div>
  );
}

export default function MobileApp() {
  const widgetsRef = useRef(null);

  // Cursor spotlight per highlight card — pointer position feeds
  // --mob-mx/--mob-my, rAF-coalesced like the site's other spotlights.
  useEffect(() => {
    const el = widgetsRef.current;
    if (!el) return;
    let raf = 0;
    const move = e => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.querySelectorAll(".mob-widget").forEach(card => {
          const r = card.getBoundingClientRect();
          card.style.setProperty("--mob-mx", `${e.clientX - r.left}px`);
          card.style.setProperty("--mob-my", `${e.clientY - r.top}px`);
        });
      });
    };
    el.addEventListener("pointermove", move);
    return () => {
      el.removeEventListener("pointermove", move);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      <SEOHead
        title="Mobile Test Management App for QA Anywhere Anytime"
        description="Manage and execute mobile tests anywhere with our mobile test management app, ensuring efficient QA, real-time tracking, and seamless test control."
        keywords="mobile test management app, QA mobile app, bug reporting app, Froth TestOps"
      />
      <div className="fd-main-wrapper page-padding">
        <Header />
        <main>
          <PageHero
            crumbs="Mobile App"
            eyebrow={
              <>
                <Smartphone size={15} aria-hidden="true" /> Mobile App
              </>
            }
            title={
              <>
                Seamless Test Management, <span>Wherever You Are</span>
              </>
            }
            lead="The Froth TestOps mobile app is built for agile teams who want to stay flexible without losing control. Manage test cases, check progress and report defects from any device, at any time, with full visibility and real-time collaboration on the go."
            visual={
              <div className="ft-phones">
                <PhoneMock src={requirementsScreen} alt="Froth TestOps mobile app requirements list" className="ft-phones__back" />
                <PhoneMock src={dashboardScreen} alt="Froth TestOps mobile app project dashboard with defect analytics" className="ft-phones__front" />
              </div>
            }
          >
            <StoreBadges />
          </PageHero>

          {/* Highlights: glass cards on an aurora stage, micro-visual per card. */}
          <section className="ft-section">
            <div className="ft-container">
              <h2 className="visually-hidden">Mobile app highlights</h2>
              <div className="mob-stage">
                <div className="mob-widgets" ref={widgetsRef}>
                  {highlights.map((h, i) => {
                    const Icon = h.icon;
                    return (
                      <div className="mob-widget" key={h.title}>
                        <span className="mob-widget__icon">
                          <Icon size={22} aria-hidden="true" />
                        </span>
                        <h3 className="mob-widget__title">{h.title}</h3>
                        <p className="mob-widget__text">{h.text}</p>
                        <div className="mob-widget__art">{widgetArt[i]}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          <section className="ft-section ft-section--dark">
            <div className="ft-container">
              <SectionHeading
                eyebrow="Screens"
                title={
                  <>
                    A closer look at the <span>app</span>
                  </>
                }
                subtitle="Swipe or scroll to see more."
              />
            </div>
            <div className="mob-gallery" tabIndex={0} aria-label="Froth TestOps mobile app screenshots">
              {screens.map((src, i) => (
                <figure className="mob-phone" key={src}>
                  <img src={src} alt={`Froth TestOps mobile app screen ${i + 1} of ${screens.length}`} loading="lazy" />
                </figure>
              ))}
            </div>
          </section>

          {/* Features as app cards with a gradient top edge. */}
          <section className="ft-section ft-section--alt">
            <div className="ft-container">
              <SectionHeading
                align="left"
                eyebrow="Features"
                title={
                  <>
                    Powerful mobile testing, <span>built for agile teams</span>
                  </>
                }
                subtitle="Execute test cases, log defects, collaborate with your team and stay on top of your projects from any device."
              />
              <div className="mob-apps">
                {features.map(f => {
                  const Icon = f.icon;
                  return (
                    <div className="mob-appcard" key={f.title}>
                      <div className="mob-appcard__head">
                        <span className="mob-appcard__icon">
                          <Icon size={20} aria-hidden="true" />
                        </span>
                        <h3 className="mob-appcard__title">{f.title}</h3>
                      </div>
                      <p className="mob-appcard__text">{f.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}
